import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableRow,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetOrdersQuery,
  useGetOrderStatusMutation,
  useDeleteOrderMutation,
  useMarkOrdersAsReadMutation,
  useResyncOrderMutation,
} from "../../redux/reducer/features/Order/orderSlice";
import { OrderStatus } from "../../models/features/Order/Order";
import PageLoader from "../app/PageLoader";
import { AppState } from "../../redux/reducer/rootReducer";
import DeleteIcon from "@mui/icons-material/Delete";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppGridId } from "../../models/app/AppGridModels";
import { GetAppGridColumns } from "../../services/utilities/utility";
import { addDealerDashboardCounts } from "../../redux/reducer/features/Dashboard/DashboardSlice";
import { OrderFilter } from "../../models/app/AppModels";
import ServerAppGrid from "../app/ServerAppGrid";
import { customPriceFilterOperators } from "../app/CustomGridFilterOpereators";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { formatDate } from "./OrderGrid";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface AdminOrderGridProps {}
const OrderGrid: React.FunctionComponent<AdminOrderGridProps> = (props) => {
  const dispatch = useDispatch();
  const stats = useSelector(
    (state: AppState) => state.dashboardReducer.dealerDashboard
  );
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find((x) => x.gridId === AppGridId.Orders)
  );
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const [tabValue, setTabValue] = useState("orders");
  const [paging, setPaging] = useState<OrderFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    archivedOnly: false,
  });

  const { data, isLoading, isFetching, refetch } = useGetOrdersQuery({
    paging: paging,
  });

  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any>();
  const [deletedOrders, setDeletedOrders] = useState<any[]>([]);
  const [statusDialog, setStatusDialog] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>();
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [ordersCount, setOrdersCount] = React.useState<number>(0);
  const [archiveOrdersCount, setArchiveOrdersCount] = React.useState<number>(0);

  const [orderStatusMutation, orderStatusMutationResult] =
    useGetOrderStatusMutation();
  const [markAsReadMutation, markAsReadMutationResult] =
    useMarkOrdersAsReadMutation();
  const [deleteOrderMutation, deleteOrderMutationResult] =
    useDeleteOrderMutation();
  const [resyncOrderMutation, resyncOrderMutationResult] =
    useResyncOrderMutation();

  const handleClose = () => {
    setDeleteConfirm(false);
    setStatusDialog(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleSetOrderStatus = (order: any) => {
    if(order){
      setOrderStatus({shopifyOrderNumber:order?.order?.shopifyOrderNumber,orderDate:order?.order?.dateCreated,dmsOrderId:order?.everestOrderDetail?.dmsOrderId,shopifyOrderId:order?.order?.shopifyOrderId,sourceType:order?.order?.sourceType,dmsError:order?.everestOrderDetail?.dmsError});
    }
    else{
      setOrderStatus(undefined);
    }
  };

  const handleDeleteOrder = (orderId: any) => {
    setDeletedOrders([
      ...deletedOrders,
      orders.find((item) => item.id === orderId),
    ]);
    setOrders(orders.filter((item) => item.id !== orderId));
    setOrdersCount(ordersCount - 1);
    setArchiveOrdersCount(archiveOrdersCount + 1);
    deleteOrderMutation({ orderId: orderId }).then((res) => {});
  };

  const handleResyncOrder = (orderId: string, setupId: string) => {
    resyncOrderMutation({ orderId, setupId })
      .then((res: any) => {
        if (res && res.data && res.data.status === 200) {
          // Handle successful resync if needed
        }
      })
      .finally(() => {
        setStatusDialog(false);
        refetch();
      });
  };

  useEffect(() => {
    if (data && data.entityList) {
      setOrders(data.entityList);
      setOrdersCount(data.totalRecords);
    }
  }, [data]);

  useEffect(() => {
    if (stats && stats.unreadOrders > 0) {
      markAsReadMutation().then((res: any) => {
        if (res && res.data) {
          dispatch(
            addDealerDashboardCounts({
              unreadOrders: 0,
              unreadQuotes: stats.unreadQuotes,
            })
          );
        }
      });
    }
  }, []);
  useEffect(() => {
    refetch();
  }, [paging]);

  const columns: GridColDef[] = [
    {
      field: "shopifyOrderNumber",
      headerName: "Shopify Order Id",
      width: 130,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.shopifyOrderNumber===0?"N/A":params.row.order.shopifyOrderNumber,
    },
    {
      field: "saleOrderId",
      headerName: "Everest Order Id",
      width: 130,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.saleOrderId,
    },
    {
      field: "dmsDealerId",
      headerName: "DMS Dealer Id",
      width: 120,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.everestOrderDetail?.dmsDealerId,
    },
    {
      field: "dealerId",
      headerName: "Dealer Id",
      width: 100,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.everestOrderDetail?.dealerId,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 180,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.customerName,
    },

    {
      field: "customerEmail",
      headerName: "Email",
      width: 220,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.customerEmail,
    },
    {
      field: "customerAddress",
      headerName: "Address",
      width: 220,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.customerAddress,
    },
    {
      field: "taxAmount",
      headerName: "Tax($)",
      width: 100,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridRenderCellParams) =>
        parseFloat(params.row.order.taxAmount).toFixed(2),
    },
    {
      field: "amount",
      headerName: "Amount($)",
      width: 130,
      align: "right",
      type: "number",
      headerAlign: "right",
      filterOperators: customPriceFilterOperators,
      valueGetter: (params: GridRenderCellParams) =>
        params.row.order.amount.toFixed(2),
    },
    {
      field: "dateCreated",
      headerName: "Date",
      type: "actions",
      width: 220,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.order.dateCreated
          ? formatDate(params.row.order.dateCreated)
          : "";
      },
    },
    {
      field: "sourceType",
      headerName: "Source",
      type: "actions",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={
              params.row.order.sourceType === "Shopify"
                ? "Shopify"
                : "Parts Portal"
            }
            size="small"
            color={
              params.row.order.sourceType === "Shopify" ? "success" : "warning"
            }
          />
        );
      },
    },
    {
      field: "Order Status",
      headerName: "DMS Status",
      type: "actions",
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        return getOrderStatusChip(params.row?.everestOrderDetail?.dmsOrderId);
      },
    },
    {
      field: "Action",
      headerName: "Action",
      type: "actions",
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <IconButton
              id="basic-button"
              onClick={(n) => {
                setSelectedOrder(params.row);
                handleSetOrderStatus(params.row);
                setStatusDialog(true);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Box display="flex" alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Orders
        </Typography>
        <IconButton onClick={() => refetch()}>
          <SyncIcon />
        </IconButton>
      </Box>
      <Card>
        <TabContext value={tabValue}>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <Tab label={"Orders"} value={"orders"} />
          </TabList>
          <TabPanel sx={{ padding: 0 }} value="orders">
            <ServerAppGrid
              getRowId={(row) => row.order.id}
              gridId={AppGridId.Orders}
              columns={GetAppGridColumns(
                columns,
                gridSetting?.columnVisibilityModel
              )}
              data={orders}
              isLoading={isLoading || isFetching}
              height={"70vh"}
              paging={paging}
              setPaging={setPaging}
              totalRecords={ordersCount}
              hideCheckBoxes={false}
            ></ServerAppGrid>
          </TabPanel>
        </TabContext>
      </Card>
      <PageLoader isLoading={orderStatusMutationResult.isLoading}></PageLoader>
      <Dialog
        open={statusDialog}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
        fullWidth
      >
        <DialogTitle align="center">DMS Status</DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          <Table aria-label="customized table">
            <TableBody>
             <StyledTableRow>
                             <StyledTableCell>Order Date</StyledTableCell>
                             <StyledTableCell>
                               {orderStatus?.orderDate !== null ||
                               orderStatus?.orderDate !== ""
                                 ? formatDate(orderStatus?.orderDate)
                                 : ""}
                             </StyledTableCell>
                           </StyledTableRow>
                           <StyledTableRow>
                             <StyledTableCell>Shopify Order Id</StyledTableCell>
                             <StyledTableCell>{orderStatus?.shopifyOrderNumber===0?"N/A":orderStatus?.shopifyOrderNumber}</StyledTableCell>
                           </StyledTableRow>
                           <StyledTableRow>
                             <StyledTableCell>DMS Order Id</StyledTableCell>
                             <StyledTableCell>{orderStatus?.dmsOrderId}</StyledTableCell>
                           </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>Order Status</StyledTableCell>
                <StyledTableCell>
                  {getOrderStatusChip(orderStatus?.dmsOrderId)}
                  {selectedOrder?.order.sourceType === "Shopify" &&
                    selectedOrder?.everestOrderDetail?.dmsOrderId === "-1" && (
                      <Chip
                        label="Resync"
                        color="primary"
                        size="small"
                        onClick={() =>
                          handleResyncOrder(
                            selectedOrder.order.id,
                            selectedOrder.order?.dmsSetupId
                          )
                        }
                        disabled={resyncOrderMutationResult.isLoading}
                        sx={{ ml: 2 }}
                        icon={
                          resyncOrderMutationResult.isLoading ? (
                            <CircularProgress size={14} />
                          ) : undefined
                        }
                      />
                    )}
                </StyledTableCell>
              </StyledTableRow>
              
            </TableBody>
          </Table>
          <Box
            sx={{
              display:
                orderStatus?.dmsOrderId === "-1" && orderStatus.dmsError
                  ? "block"
                  : "none",
            }}
          >
            <Alert severity="error">{orderStatus?.dmsError ?? ""}</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
          variant="outlined"
            size="small"
            onClick={() => {
              setStatusDialog(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirm}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
        fullWidth
      >
        <DialogTitle align="center">Confirm to Delete</DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          This order will be archived. You will be able to see it in Archive
          Orders tab.
          <br />
          Are you sure to delete this order?
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            type="submit"
            onClick={() => {
              handleDeleteOrder(selectedOrderId);
              setDeleteConfirm(false);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderGrid;

const getOrderStatusChip = (status: any) => {
  if (status && status !== "-1")
    return <Chip label="Order created" color="success" size="small" />;
  else if (status === "-1")
    return <Chip label="Failed" color="error" size="small" />;
  else return <Chip label="Pending" color="warning" size="small" />;
};
